import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Formik } from "formik";
import { PatternFormat } from "react-number-format";
import { useDropzone } from "react-dropzone";
import * as CF from "../../config/function";
import { enum_api_uri } from "../../config/enum";
import { confirmPop, termsPop, termsCheckList } from "../../store/popupSlice";
import ConfirmPop from "../../components/popup/ConfirmPop";

/*
 * 1% MODEL 지원 폼
 * - OnePercentApply.js(사진 업로드 + 비회원 신청) 로직을 뼈대로 복제, 디자인은 1% MODEL 랜딩(model.css)에 맞춤.
 * - 백엔드 계약 재사용(변경 없음):
 *   · 제출 POST date_apply(/v1/main/requset)  body: {name, year, gender, tel, is_photo:true, photo:[파일명], is_not_address:true, idx}
 *   · 사진 업로드 POST vip_apply_img(/v1/main/vip-request/image-add) → {mediaUrls:[...]}
 *   · 사진 삭제 DELETE vip_apply_img_delt(/v1/main/vip-request/image-remove/:filename)
 * - idx=19 고정: 신규 LANDING_OFFICIAL row(모델 심사형, JOINSTYLE='2'). DB row는 별도 단계에서 seed.
 */

// 이 폼 전용 funnel idx (LANDING_OFFICIAL 신규 행) — DB seed는 별도 단계
const APPLY_IDX = 19;
// 사진 최대 첨부 수(서버 검증 3~30장)
const MAX_PHOTO = 30;

const ModelApply = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const popup = useSelector((state) => state.popup);
    const date_apply = enum_api_uri.date_apply;
    const vip_apply_img = enum_api_uri.vip_apply_img;
    const vip_apply_img_delt = enum_api_uri.vip_apply_img_delt;

    const [confirm, setConfirm] = useState(false);
    const [applyOkConfirm, setApplyOkConfirm] = useState(false);
    const [yearList, setYearList] = useState([]);
    const [yearSelected, setYearSelected] = useState(false);
    const [imgNameList, setImgNameList] = useState([]);
    const [imgList, setImgList] = useState([]);
    const [isAllChecked, setIsAllChecked] = useState(false);
    const [checkedItems, setCheckedItems] = useState([]);

    const termsList = [
        { value: 1, txt: "개인정보 보호정책 동의" },
        { value: 3, txt: "개인정보 수집 및 이용동의" },
        { value: 4, txt: "이용약관 동의" },
        { value: 6, txt: "광고성 메시지 수신 동의" },
    ];

    // Confirm팝업 닫힐때
    useEffect(() => {
        if (popup.confirmPop === false) {
            setConfirm(false);
            setApplyOkConfirm(false);
        }
    }, [popup.confirmPop]);

    // 나이 년도 구하기 (만 19세 ~ 1963년생)
    const getYearList = () => {
        const currentYear = new Date().getFullYear();
        const startYear = currentYear - 19;
        const endYear = 1963;

        const yearsArray = [];
        for (let i = startYear; i >= endYear; i--) {
            yearsArray.push(i.toString());
        }
        setYearList([...yearsArray]);
    };

    useEffect(() => {
        getYearList();
    }, []);

    //이미지 첨부-----------------------------------------
    // 프로필사진 등록
    const { getRootProps, getInputProps } = useDropzone({
        accept: {
            "image/*": [],
        },
        multiple: true,
        onDrop: (acceptedFiles) => {
            const files = acceptedFiles.length + imgList.length;

            if (acceptedFiles.length === 0) {
                return;
            } else if (files > MAX_PHOTO) {
                dispatch(
                    confirmPop({
                        confirmPop: true,
                        confirmPopTit: "알림",
                        confirmPopTxt: `사진은 최대 ${MAX_PHOTO}장까지 첨부 가능합니다.`,
                        confirmPopBtn: 1,
                    })
                );
                setConfirm(true);
            } else {
                const formData = new FormData();
                acceptedFiles.forEach((item) => {
                    formData.append("media", item);
                });

                axios
                    .post(vip_apply_img, formData, {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    })
                    .then((res) => {
                        if (res.status === 201) {
                            const mediaUrls = res.data.mediaUrls;
                            const newList = acceptedFiles.map((file, index) => ({
                                name: file.name,
                                url: mediaUrls[index],
                            }));
                            setImgList((prevList) => [...prevList, ...newList]);
                        }
                    })
                    .catch((error) => {
                        const err_msg = CF.errorMsgHandler(error);
                        dispatch(
                            confirmPop({
                                confirmPop: true,
                                confirmPopTit: "알림",
                                confirmPopTxt: err_msg,
                                confirmPopBtn: 1,
                            })
                        );
                        setConfirm(true);
                    });
            }
        },
    });

    //이미지 삭제
    const handleRemove = (idx, url) => {
        const filename = url.substring(url.lastIndexOf("/") + 1);

        axios
            .delete(vip_apply_img_delt.replace(":filename", filename))
            .then((res) => {
                if (res.status === 200) {
                    let newList = [...imgList];
                    newList.splice(idx, 1);
                    setImgList(newList);
                }
            })
            .catch((error) => {
                const err_msg = CF.errorMsgHandler(error);
                dispatch(
                    confirmPop({
                        confirmPop: true,
                        confirmPopTit: "알림",
                        confirmPopTxt: err_msg,
                        confirmPopBtn: 1,
                    })
                );
                setConfirm(true);
            });
    };

    // 프로필사진 미리보기 생성
    const profileImgs = imgList.map((img, i) => (
        <li key={i} className="mlp_apply_file">
            <p className="mlp_apply_file_name">
                <span>{img.name}</span>
            </p>
            <button
                type="button"
                className="mlp_apply_file_delt"
                onClick={() => handleRemove(i, img.url)}
            >
                삭제
            </button>
        </li>
    ));

    //이미지이름만 배열로
    useEffect(() => {
        const newNameList = imgList.map((item) => item.url.split("/").pop());
        setImgNameList(newNameList);
    }, [imgList]);
    //이미지 첨부-----------------------------------------

    //전체약관 동의
    const allAgreeHandler = (checked) => {
        const allTerms = ["terms1", "terms3", "terms4", "terms6"];
        setIsAllChecked(checked);
        setCheckedItems(checked ? allTerms : []);
    };

    //약관동의
    const agreeHandler = (checked, value) => {
        if (checked) {
            setCheckedItems([...checkedItems, value]);
        } else if (!checked && checkedItems.includes(value)) {
            setCheckedItems(checkedItems.filter((el) => el !== value));
        }
    };

    //약관동의 다하면 전체약관동의 체크박스 체크됨
    useEffect(() => {
        setIsAllChecked(checkedItems.length === 4);
        dispatch(termsCheckList(checkedItems));
    }, [checkedItems]);

    useEffect(() => {
        setCheckedItems(popup.termsCheckList);
    }, [popup.termsCheckList]);

    //1% 모델 지원하기
    const submit = (values) => {
        const tel = values.tel.replace(/[^0-9]/g, "");

        if (!values.name) {
            dispatch(
                confirmPop({
                    confirmPop: true,
                    confirmPopTit: "알림",
                    confirmPopTxt: "이름을 입력해주세요.",
                    confirmPopBtn: 1,
                })
            );
            setConfirm(true);
        } else if (!values.gender) {
            dispatch(
                confirmPop({
                    confirmPop: true,
                    confirmPopTit: "알림",
                    confirmPopTxt: "성별을 선택해주세요.",
                    confirmPopBtn: 1,
                })
            );
            setConfirm(true);
        } else if (!tel || tel.length < 11) {
            dispatch(
                confirmPop({
                    confirmPop: true,
                    confirmPopTit: "알림",
                    confirmPopTxt: "전화번호를 입력해주세요.",
                    confirmPopBtn: 1,
                })
            );
            setConfirm(true);
        } else if (!values.year) {
            dispatch(
                confirmPop({
                    confirmPop: true,
                    confirmPopTit: "알림",
                    confirmPopTxt: "출생연도를 선택해주세요.",
                    confirmPopBtn: 1,
                })
            );
            setConfirm(true);
        } else if (imgList.length < 3) {
            dispatch(
                confirmPop({
                    confirmPop: true,
                    confirmPopTit: "알림",
                    confirmPopTxt: "정면사진을 3장 이상 첨부해주세요.",
                    confirmPopBtn: 1,
                })
            );
            setConfirm(true);
        } else if (!isAllChecked) {
            dispatch(
                confirmPop({
                    confirmPop: true,
                    confirmPopTit: "알림",
                    confirmPopTxt: "전체동의 체크 해주세요.",
                    confirmPopBtn: 1,
                })
            );
            setConfirm(true);
        } else {
            const body = {
                name: values.name,
                year: values.year,
                gender: values.gender,
                tel: tel,
                is_photo: true,
                photo: imgNameList,
                is_not_address: true,
                idx: APPLY_IDX,
            };
            axios
                .post(`${date_apply}`, body)
                .then((res) => {
                    if (res.status === 200) {
                        setApplyOkConfirm(true);
                        dispatch(
                            confirmPop({
                                confirmPop: true,
                                confirmPopTit: "알림",
                                confirmPopTxt: "지원이 완료됐습니다.",
                                confirmPopBtn: 1,
                            })
                        );
                    }
                })
                .catch((error) => {
                    const err_msg = CF.errorMsgHandler(error);
                    dispatch(
                        confirmPop({
                            confirmPop: true,
                            confirmPopTit: "알림",
                            confirmPopTxt: err_msg,
                            confirmPopBtn: 1,
                        })
                    );
                    setConfirm(true);
                });
        }
    };

    return (
        <div className="model_lp">
            <div className="mlp_apply">
                {/* 상단 헤더 */}
                <div className="mlp_apply_head">
                    <button
                        type="button"
                        className="mlp_apply_logo"
                        onClick={() => navigate("/model")}
                    >
                        ONE PICK
                    </button>
                    <h1 className="mlp_apply_tit">1% 모델 지원하기</h1>
                    <p className="mlp_apply_sub">
                        아래 항목을 정확하게 입력해주세요.
                        <br />
                        심사 후 제공해주신 연락처로 개별 안내드립니다.
                    </p>
                </div>

                {/* 지원 폼 */}
                <div className="mlp_apply_box">
                    <Formik
                        initialValues={{
                            name: "",
                            year: "",
                            gender: "",
                            tel: "",
                        }}
                    >
                        {({ values, handleChange, setFieldValue }) => (
                            <form>
                                <ul className="mlp_apply_ul">
                                    <li>
                                        <p className="mlp_apply_label">
                                            이름{" "}
                                            <span className="mlp_apply_req">
                                                *
                                            </span>
                                        </p>
                                        <div className="mlp_apply_input">
                                            <input
                                                type="text"
                                                placeholder="이름을 입력해주세요."
                                                name="name"
                                                value={values.name}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </li>
                                    <li>
                                        <p className="mlp_apply_label">
                                            성별{" "}
                                            <span className="mlp_apply_req">
                                                *
                                            </span>
                                        </p>
                                        <ul className="mlp_apply_gender">
                                            <li>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="gender"
                                                        checked={
                                                            values.gender === "1"
                                                        }
                                                        onChange={() => {
                                                            setFieldValue(
                                                                "gender",
                                                                "1"
                                                            );
                                                        }}
                                                    />
                                                    <span className="box">
                                                        남성
                                                    </span>
                                                </label>
                                            </li>
                                            <li>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="gender"
                                                        checked={
                                                            values.gender === "2"
                                                        }
                                                        onChange={() => {
                                                            setFieldValue(
                                                                "gender",
                                                                "2"
                                                            );
                                                        }}
                                                    />
                                                    <span className="box">
                                                        여성
                                                    </span>
                                                </label>
                                            </li>
                                        </ul>
                                    </li>
                                    <li>
                                        <p className="mlp_apply_label">
                                            전화번호{" "}
                                            <span className="mlp_apply_req">
                                                *
                                            </span>
                                        </p>
                                        <div className="mlp_apply_input">
                                            <PatternFormat
                                                format="###-####-####"
                                                name="tel"
                                                value={values.tel}
                                                onChange={handleChange}
                                                placeholder="숫자만 입력해주세요."
                                            />
                                        </div>
                                    </li>
                                    <li>
                                        <p className="mlp_apply_label">
                                            출생연도{" "}
                                            <span className="mlp_apply_req">
                                                *
                                            </span>
                                        </p>
                                        <div className="mlp_apply_input">
                                            <select
                                                name="year"
                                                value={values.year}
                                                onChange={(e) => {
                                                    handleChange(e);
                                                    setYearSelected(true);
                                                }}
                                                className={
                                                    yearSelected
                                                        ? "selected"
                                                        : ""
                                                }
                                            >
                                                <option value="" hidden>
                                                    출생연도를 선택해주세요.
                                                </option>
                                                {yearList.map((cont, i) => (
                                                    <option value={cont} key={i}>
                                                        {cont} 년생
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </li>
                                    <li>
                                        <p className="mlp_apply_label">
                                            정면사진{" "}
                                            <span className="mlp_apply_req">
                                                *
                                            </span>
                                        </p>
                                        <div
                                            {...getRootProps({
                                                className: "mlp_apply_drop",
                                            })}
                                        >
                                            <input {...getInputProps()} />
                                            <div className="mlp_apply_drop_inner">
                                                <p>사진을 첨부해주세요.</p>
                                                <button type="button">
                                                    사진 첨부
                                                </button>
                                            </div>
                                        </div>
                                        <div className="mlp_apply_file_box">
                                            <p className="mlp_apply_file_guide">
                                                정면 사진은 꼭 3장 이상
                                                첨부해주세요! (최대 {MAX_PHOTO}장)
                                            </p>
                                            {profileImgs.length > 0 && (
                                                <ul className="mlp_apply_file_list">
                                                    {profileImgs}
                                                </ul>
                                            )}
                                        </div>
                                    </li>
                                </ul>

                                <div className="mlp_apply_terms">
                                    <div className="mlp_apply_all_check">
                                        <label htmlFor="agreeAll">
                                            <input
                                                type="checkbox"
                                                onChange={(e) => {
                                                    allAgreeHandler(
                                                        e.currentTarget.checked
                                                    );
                                                }}
                                                checked={isAllChecked}
                                                id="agreeAll"
                                            />
                                            <span className="check" />
                                            <span className="txt">
                                                전체동의{" "}
                                                <span className="mlp_apply_req">
                                                    *
                                                </span>
                                            </span>
                                        </label>
                                    </div>
                                    <ul className="mlp_apply_terms_ul">
                                        {termsList.map((cont, i) => (
                                            <li key={`terms_${i}`}>
                                                <div className="mlp_apply_check">
                                                    <label
                                                        htmlFor={`terms${cont.value}`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            onChange={(e) => {
                                                                agreeHandler(
                                                                    e
                                                                        .currentTarget
                                                                        .checked,
                                                                    e
                                                                        .currentTarget
                                                                        .id
                                                                );
                                                            }}
                                                            checked={checkedItems.includes(
                                                                `terms${cont.value}`
                                                            )}
                                                            id={`terms${cont.value}`}
                                                        />
                                                        <span className="check" />
                                                        <span className="txt">
                                                            {cont.txt}
                                                        </span>
                                                    </label>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="mlp_apply_terms_view"
                                                    onClick={() => {
                                                        dispatch(
                                                            termsPop({
                                                                termsPop: true,
                                                                termsPopIdx:
                                                                    cont.value,
                                                            })
                                                        );
                                                    }}
                                                >
                                                    보기
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <button
                                    type="button"
                                    className="mlp_btn mlp_btn_primary mlp_apply_submit"
                                    onClick={() => {
                                        submit(values);
                                    }}
                                >
                                    1% 모델 지원하기
                                </button>
                            </form>
                        )}
                    </Formik>
                </div>
            </div>

            {/* confirm팝업 */}
            {confirm && <ConfirmPop />}

            {/* 지원완료 confirm팝업 */}
            {applyOkConfirm && (
                <ConfirmPop
                    closePop="custom"
                    onCloseHandler={() => navigate("/model")}
                />
            )}
        </div>
    );
};

export default ModelApply;
